import TableProductsComponent from "@/components/sharedComponents/tableProducts/TableProductsComponent";
import { Order, Refund } from "@/interfaces/order";
import { RetailSettings } from "@/interfaces/retailSettings";
import {
  RefundHistoryFields,
  SalesPreviousCustomerTableMetadata
} from "@/metadata/order";
import { salesHistoryFieldsFunction } from "@/metadata/salesHistory";
import { metrcEnabled } from "@/router.utils";
import { messagesService } from "@/services/messages.service";
import { orderService } from "@/services/order.service";
import { usersService } from "@/services/user.service";
import {
  TableComponent,
  TableHeader,
  TablePagination,
  TablePaginationDefault
} from "helix-vue-components";
import { Component, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import Template from "./CustomerHistory.template.vue";

@Component({
  mixins: [Template],
  components: {
    TableProductsComponent,
    TableComponent
  }
})
export default class CustomerHistoryComponent extends Vue {
  public pagination: TablePagination = { ...TablePaginationDefault };
  public loading: boolean = false;
  public ordersData: Order[] = [];
  public refundData: Refund[] = [];
  public hasMetrc = metrcEnabled();
  public headers: TableHeader[] = [];
  public fieldsToShow: Array<{ [key: string]: string }> = [];
  public refundFieldsToShow: Array<{ [key: string]: string }> = [];
  @Getter("currentRetailSettings", { namespace: "AuthModule" })
  public retailSettings!: RetailSettings;
  @Getter("hasBioTrackTraceIntegrations", { namespace: "AuthModule" })
  public hasBioTrackTraceIntegrations!: boolean;
  @Getter("bioTrackTraceEnabled", { namespace: "AuthModule" })
  public bioTrackTraceEnabled!: boolean;
  public tableData: Order[] = [];
  public refundTableData: Refund[] = [];

  public parsedOrders() {
    this.tableData = this.ordersData.map(order => ({
      ...order,
      metrc_reciept_number: `${order.status}`,
      total: order.total,
      sub_total: order.sub_total,
      tax_total: order.tax_total,
      line_order_discounts: order.line_order_discounts,
      updated_at: order.sold_at,
      order_items:
        order.order_items &&
        order.order_items.map(item => ({
          ...item,
          available: +item.available!.toFixed(2),
          price_final: item.price_final,
          quantityFormated: `${item.quantity}${item.product_info &&
            item.product_info.product_unit}`
        }))
    }));
  }

  public parsedRefunds() {
    this.refundTableData = this.refundData.map(order => ({
      ...order,
      metrc_reciept_number:
        order.items &&
        order.items.map(item => `${item.order_items!.order!.status}`)[0],
      total: order.total,
      sub_total: order.sub_total,
      tax_total: order.tax_total,
      line_order_discounts: order.line_order_discounts,
      price_raw: order.items.map(subItem => {
        subItem.price_raw =
          subItem.quantity * subItem.order_items!.unit_base_price!;
      }),
      order_items:
        order.items &&
        order.items.map(item => ({
          ...item.order_items,
          available: +item.order_items!.available!.toFixed(2),
          price_final: item.order_items!.price_final,
          quantityFormated: `${item.order_items!.quantity}${item.order_items!
            .product_info && item.order_items!.product_info.product_unit}`
        })),
      order_number: order.operation_uid
    }));
  }

  protected async mounted() {
    this.headers = SalesPreviousCustomerTableMetadata(this.hasMetrc);
    this.fieldsToShow = salesHistoryFieldsFunction(
      this.retailSettings.stock_identification_type_in_pos
    );
    this.refundFieldsToShow = RefundHistoryFields(
      this.retailSettings.stock_identification_type_in_pos
    );
    await this.loadData(this.pagination);
    await this.loadRefundData(this.pagination);
  }
  private async loadData(pagination: TablePagination) {
    this.loading = true;
    try {
      const response = await orderService.getOrdersPaidOfCustomer(
        this.$route.params.id ? this.$route.params.id : "",
        pagination
      );
      this.pagination = await orderService.getPagination(true);
      delete this.pagination.changeItemsPerPage;
      this.ordersData = response.data.data;
      const usersIds = [];
      for (const order of this.ordersData) {
        if (order.user_id) {
          usersIds.push(order.user_id);
        }
      }
      const users = await usersService.getUsers(usersIds);
      const usersMap = new Map();
      for (const user of users) {
        usersMap.set(user.id, user);
      }
      if (this.ordersData[0]) {
        this.ordersData[0].order_items!.map(order => {
          if (
            this.hasBioTrackTraceIntegrations &&
            this.bioTrackTraceEnabled &&
            order.biotrack_traceability_id !== null
          ) {
            order.batch_barcode_uid = order.biotrack_traceability_id;
          }
        });
      }
      for (const order of this.ordersData) {
        if (order.user_id) {
          order.user = usersMap.get(order.user_id);
        }
      }
      this.parsedOrders();
    } catch (e) {
      messagesService.renderErrorMessage(e);
    } finally {
      this.loading = false;
    }
  }
  private async loadRefundData(pagination: TablePagination) {
    this.loading = true;
    try {
      const refunds = await orderService.getRefundsOfCustomer(
        this.$route.params.id ? this.$route.params.id : "",
        pagination
      );
      this.pagination = await orderService.getPagination(true);
      delete this.pagination.changeItemsPerPage;
      this.refundData = refunds.data.data;
      const usersIds = [];
      for (const refund of this.refundData) {
        if (refund.user_id) {
          usersIds.push(refund.user_id);
        }
      }
      const users = await usersService.getUsers(usersIds);
      const usersMap = new Map();
      for (const user of users) {
        usersMap.set(user.id, user);
      }
      if (this.refundData[0]) {
        this.refundData[0].items!.map(order => {
          if (
            this.hasBioTrackTraceIntegrations &&
            this.bioTrackTraceEnabled &&
            order.order_items!.biotrack_traceability_id !== null
          ) {
            order.order_items!.batch_barcode_uid = order.order_items!.biotrack_traceability_id;
          }
        });
      }
      for (const order of this.refundData) {
        if (order.user_id) {
          order.user = usersMap.get(order.user_id);
        }
      }
      this.parsedRefunds();
    } catch (e) {
      messagesService.renderErrorMessage(e);
    } finally {
      this.loading = false;
    }
  }
}
